import React, { useCallback, useEffect, useMemo, useState } from "react";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  AlertTriangle,
  Ban,
  Building2,
  CheckCircle2,
  Edit3,
  Globe2,
  KeyRound,
  Link2,
  Plus,
  Power,
  RefreshCw,
  Search,
  Server,
  ShieldCheck,
  Trash2,
  UserMinus,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { apiUrl } from "../api";

const TEXT = {
  en: {
    title: "Yanaytech Platform Management",
    subtitle: "Super Admin console for customers, ownership, sites and TagWeigh systems.",
    overview: "Overview",
    customers: "Customers",
    sites: "Sites",
    devices: "Devices / Systems",
    active: "Active",
    blocked: "Blocked",
    enabled: "Enabled",
    disabled: "Disabled",
    unassigned: "Unassigned",
    assigned: "Assigned",
    totalCustomers: "Customers",
    activeCustomers: "Active Customers",
    blockedCustomers: "Blocked Customers",
    totalSites: "Sites",
    unassignedSites: "Unassigned Sites",
    totalDevices: "Devices",
    enabledDevices: "Enabled Devices",
    disabledDevices: "Disabled Devices",
    search: "Search",
    allStatuses: "All statuses",
    createCustomer: "Create Customer",
    customerName: "Customer name",
    legalName: "Legal name",
    businessId: "Business / Company ID",
    country: "Country",
    address: "Address",
    contactName: "Primary contact name",
    contactPhone: "Phone",
    contactEmail: "Email",
    internalNotes: "Internal Yanaytech notes",
    profile: "Profile",
    editProfile: "Edit Profile",
    saveProfile: "Save Profile",
    cancel: "Cancel",
    deleteCustomer: "Delete Customer",
    deleteCustomerWarning:
      "This action is intended only for an empty customer and cannot be undone.",
    deleteRejected:
      "This customer cannot be deleted because it contains assigned resources or historical data. Block the customer instead if access should be disabled.",
    internal: "Internal",
    noProfileFields: "No additional profile details saved.",
    create: "Create",
    refresh: "Refresh",
    openDetails: "Open details",
    status: "Status",
    admins: "Admins",
    customerAdmins: "Customer Admins",
    assignAdmin: "Assign Admin",
    unassignAdmin: "Unassign Admin",
    noAdmins: "No Customer Admin assigned.",
    availableAdmins: "Eligible unassigned Admins",
    blockCustomer: "Block Customer",
    reactivateCustomer: "Reactivate Customer",
    blockWarning:
      "Blocking this customer will stop human access for its users. Data, sites and devices are preserved, and physical devices are not automatically disabled.",
    reactivateWarning: "Reactivate this customer and restore human access?",
    details: "Details",
    summary: "Summary",
    noCustomers: "No customers found.",
    noSites: "No sites found.",
    noDevices: "No devices found.",
    assignedSites: "Customer Sites",
    assignSite: "Assign Site",
    unassignSite: "Unassign Site",
    moveSite: "Move Site",
    siteWarning:
      "This site ownership change may clear legacy user assignments for the site to prevent cross-customer access.",
    site: "Site",
    customer: "Customer",
    deviceCount: "Devices",
    animalCount: "Animals",
    assignment: "Assignment",
    selectCustomer: "Select customer",
    selectSite: "Select site",
    provisionDevice: "Provision Device",
    deviceMac: "Device MAC",
    siteMac: "Site MAC",
    deviceName: "Device name",
    serialNumber: "Serial number",
    firmwareVersion: "Firmware version",
    provision: "Provision",
    assignDevice: "Assign to Site",
    rotateToken: "Rotate Token",
    enable: "Enable",
    disable: "Disable",
    oneTimeToken: "One-time Device Token",
    tokenNotice: "Save this token now. It will not be shown again.",
    close: "Close",
    error: "Action failed",
    loading: "Loading platform data...",
    attention: "Needs Attention",
    noAttention: "No onboarding or blocked-customer items need attention.",
    globalMap: "Global Platform Map",
    globalMapSubtitle: "All mapped TagWeigh sites available to the platform account.",
    mappedSites: "Mapped sites",
    unmappedSites: "Sites without GPS",
    noMappedSites: "No mapped site coordinates yet.",
    noMappedSitesBody: "Sites will appear here after latitude and longitude are recorded.",
    online: "Online",
    warning: "Warning",
    offline: "Offline",
    deviceState: "Device state",
    authSeen: "Auth seen",
    mac: "MAC",
    lastContact: "Last contact",
    notAvailable: "--",
  },
  he: {
    title: "ניהול פלטפורמת Yanaytech",
    subtitle: "קונסולת Super Admin לניהול לקוחות, בעלות, אתרים ומערכות TagWeigh.",
    overview: "סקירה",
    customers: "לקוחות",
    sites: "אתרים",
    devices: "מכשירים / מערכות",
    active: "פעיל",
    blocked: "חסום",
    enabled: "מופעל",
    disabled: "מושבת",
    unassigned: "לא משויך",
    assigned: "משויך",
    totalCustomers: "לקוחות",
    activeCustomers: "לקוחות פעילים",
    blockedCustomers: "לקוחות חסומים",
    totalSites: "אתרים",
    unassignedSites: "אתרים לא משויכים",
    totalDevices: "מכשירים",
    enabledDevices: "מכשירים פעילים",
    disabledDevices: "מכשירים מושבתים",
    search: "חיפוש",
    allStatuses: "כל הסטטוסים",
    createCustomer: "יצירת לקוח",
    customerName: "שם לקוח",
    legalName: "שם משפטי",
    businessId: "מספר חברה / עסק",
    country: "מדינה",
    address: "כתובת",
    contactName: "שם איש קשר ראשי",
    contactPhone: "טלפון",
    contactEmail: "אימייל",
    internalNotes: "הערות פנימיות של Yanaytech",
    profile: "פרופיל",
    editProfile: "עריכת פרופיל",
    saveProfile: "שמירת פרופיל",
    cancel: "ביטול",
    deleteCustomer: "מחיקת לקוח",
    deleteCustomerWarning:
      "פעולה זו מיועדת רק ללקוח ריק ואינה ניתנת לביטול.",
    deleteRejected:
      "לא ניתן למחוק לקוח זה משום שהוא מכיל שיוכים או נתונים היסטוריים. יש לחסום את הלקוח אם צריך להשבית גישה.",
    internal: "פנימי",
    noProfileFields: "לא נשמרו פרטי פרופיל נוספים.",
    create: "צור",
    refresh: "רענן",
    openDetails: "פתח פרטים",
    status: "סטטוס",
    admins: "מנהלים",
    customerAdmins: "מנהלי לקוח",
    assignAdmin: "שייך Admin",
    unassignAdmin: "בטל שיוך Admin",
    noAdmins: "לא משויך Admin ללקוח.",
    availableAdmins: "חשבונות Admin פנויים",
    blockCustomer: "חסום לקוח",
    reactivateCustomer: "הפעל לקוח",
    blockWarning:
      "חסימת הלקוח תחסום גישת משתמשים אנושיים. הנתונים, האתרים והמכשירים נשמרים, ומכשירים פיזיים לא יושבתו אוטומטית.",
    reactivateWarning: "להפעיל מחדש את הלקוח ולאפשר גישה אנושית?",
    details: "פרטים",
    summary: "סיכום",
    noCustomers: "לא נמצאו לקוחות.",
    noSites: "לא נמצאו אתרים.",
    noDevices: "לא נמצאו מכשירים.",
    assignedSites: "אתרי הלקוח",
    assignSite: "שייך אתר",
    unassignSite: "בטל שיוך אתר",
    moveSite: "העבר אתר",
    siteWarning:
      "שינוי בעלות אתר עשוי לנקות שיוכי משתמשים ישנים לאתר כדי למנוע גישה בין לקוחות.",
    site: "אתר",
    customer: "לקוח",
    deviceCount: "מכשירים",
    animalCount: "בעלי חיים",
    assignment: "שיוך",
    selectCustomer: "בחר לקוח",
    selectSite: "בחר אתר",
    provisionDevice: "הקצאת מכשיר",
    deviceMac: "MAC מכשיר",
    siteMac: "MAC אתר",
    deviceName: "שם מכשיר",
    serialNumber: "מספר סידורי",
    firmwareVersion: "גרסת קושחה",
    provision: "הקצה",
    assignDevice: "שייך לאתר",
    rotateToken: "החלף טוקן",
    enable: "הפעל",
    disable: "השבת",
    oneTimeToken: "טוקן חד-פעמי למכשיר",
    tokenNotice: "יש לשמור את הטוקן עכשיו. הוא לא יוצג שוב.",
    close: "סגור",
    error: "הפעולה נכשלה",
    loading: "טוען נתוני פלטפורמה...",
    attention: "דורש טיפול",
    noAttention: "אין לקוחות חסומים או משאבי אונבורדינג שמצריכים טיפול.",
    globalMap: "מפת פלטפורמה גלובלית",
    globalMapSubtitle: "כל אתרי TagWeigh עם מיקום שזמינים לחשבון הפלטפורמה.",
    mappedSites: "אתרים במפה",
    unmappedSites: "אתרים ללא GPS",
    noMappedSites: "אין עדיין קואורדינטות לאתרים.",
    noMappedSitesBody: "אתרים יופיעו כאן לאחר שמירת latitude ו-longitude.",
    online: "מחובר",
    warning: "אזהרה",
    offline: "לא מחובר",
    deviceState: "מצב מכשיר",
    authSeen: "אימות אחרון",
    mac: "MAC",
    lastContact: "קשר אחרון",
    notAvailable: "--",
  },
};

const statusTone = {
  active: "border-[#BDEBD0] bg-[#F0FFF6] text-[#208653]",
  blocked: "border-[#F2C7C2] bg-[#FFF1EF] text-[#B43B31]",
  enabled: "border-[#BDEBD0] bg-[#F0FFF6] text-[#208653]",
  disabled: "border-[#F2C7C2] bg-[#FFF1EF] text-[#B43B31]",
  assigned: "border-[#D7E5FF] bg-[#F8FBFF] text-[#1C3FE6]",
  unassigned: "border-[#F4D7A1] bg-[#FFF8E8] text-[#9A6200]",
};

const CUSTOMER_PROFILE_FIELDS = [
  { key: "name", labelKey: "customerName", required: true },
  { key: "legal_name", labelKey: "legalName" },
  { key: "business_id", labelKey: "businessId" },
  { key: "country", labelKey: "country" },
  { key: "address", labelKey: "address", multiline: true },
  { key: "contact_name", labelKey: "contactName" },
  { key: "contact_phone", labelKey: "contactPhone" },
  { key: "contact_email", labelKey: "contactEmail", type: "email" },
  { key: "internal_notes", labelKey: "internalNotes", multiline: true, internal: true },
];

const emptyCustomerProfile = () =>
  CUSTOMER_PROFILE_FIELDS.reduce((profile, field) => {
    profile[field.key] = "";
    return profile;
  }, {});

const customerToProfile = (customer = {}) =>
  CUSTOMER_PROFILE_FIELDS.reduce((profile, field) => {
    profile[field.key] = customer[field.key] || "";
    return profile;
  }, {});

const profileToPayload = (profile) =>
  CUSTOMER_PROFILE_FIELDS.reduce((payload, field) => {
    payload[field.key] = String(profile[field.key] ?? "").trim();
    return payload;
  }, {});

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("userToken")}`,
});

const parseJson = async (response) => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || payload.message || `HTTP ${response.status}`);
  }
  return payload;
};

const normalizeText = (value) => String(value ?? "").toLowerCase();
const WORLD_CENTER = [20, 0];
const WORLD_ZOOM = 2;
const ONLINE_HEARTBEAT_TIMEOUT_SECONDS = 120;
const CARTO_TILE_URL = (process.env.REACT_APP_CARTO_TILE_URL || "").trim();
const CARTO_API_KEY = (process.env.REACT_APP_CARTO_API_KEY || "").trim();
const CARTO_VOYAGER_URL =
  CARTO_TILE_URL ||
  (CARTO_API_KEY
    ? `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(
        CARTO_API_KEY
      )}`
    : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png");
const MAP_ATTRIBUTION =
  CARTO_TILE_URL || CARTO_API_KEY
    ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
    : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const getMapTileConfig = (language) => ({
  languageKey: language === "en" ? "en" : "he-fallback-en",
  url: CARTO_VOYAGER_URL,
  attribution: MAP_ATTRIBUTION,
});

const getSiteCoordinates = (site) => {
  const lat = Number(site?.latitude);
  const lng = Number(site?.longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }

  return [lat, lng];
};

const isSiteOnline = (lastHeartbeat) => {
  if (!lastHeartbeat) return false;
  const normalized = String(lastHeartbeat).replace(" ", "T");
  const time = new Date(normalized.endsWith("Z") ? normalized : `${normalized}Z`);
  if (!Number.isFinite(time.getTime())) return false;
  return (Date.now() - time.getTime()) / 1000 <= ONLINE_HEARTBEAT_TIMEOUT_SECONDS;
};

const getSiteMapStatus = (site) => {
  if (!isSiteOnline(site?.last_heartbeat)) {
    return "offline";
  }

  const signal = Number(site?.cellular_signal);
  if (
    normalizeText(site?.status) === "warning" ||
    (Number.isFinite(signal) && signal >= 0 && signal <= 9)
  ) {
    return "warning";
  }

  return "online";
};

const isDeviceEnabled = (device) => {
  const enabled = device?.enabled;
  return enabled === true || String(enabled) === "1" || normalizeText(enabled ?? device?.status) === "enabled";
};

const siteMarkerIcon = (status) =>
  L.divIcon({
    className: "",
    html: `<span class="tw-site-marker tw-site-marker-${status}"></span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });

const formatDisplayDate = (value) => {
  if (!value) return "--";
  return String(value);
};

const MasterDashboard = ({ language = "he", initialView = "overview" }) => {
  const t = TEXT[language] || TEXT.he;
  const [view, setView] = useState(initialView);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerDetails, setCustomerDetails] = useState({});
  const [sites, setSites] = useState([]);
  const [devices, setDevices] = useState([]);
  const [onboarding, setOnboarding] = useState({ unassigned_sites: [], unassigned_devices: [], admin_candidates: [] });
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerStatus, setCustomerStatus] = useState("all");
  const [siteSearch, setSiteSearch] = useState("");
  const [siteAssignmentFilter, setSiteAssignmentFilter] = useState("all");
  const [deviceSearch, setDeviceSearch] = useState("");
  const [createCustomerProfile, setCreateCustomerProfile] = useState(emptyCustomerProfile);
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [editCustomerProfile, setEditCustomerProfile] = useState(emptyCustomerProfile);
  const [assignAdminId, setAssignAdminId] = useState("");
  const [assignSiteMac, setAssignSiteMac] = useState("");
  const [globalAssignCustomerId, setGlobalAssignCustomerId] = useState("");
  const [globalAssignSiteMac, setGlobalAssignSiteMac] = useState("");
  const [deviceSiteTargets, setDeviceSiteTargets] = useState({});
  const [provisionForm, setProvisionForm] = useState({
    mac: "",
    site_mac: "",
    device_name: "",
    serial_number: "",
    firmware_version: "",
  });
  const [oneTimeToken, setOneTimeToken] = useState(null);

  useEffect(() => setView(initialView), [initialView]);

  const customerById = useMemo(() => {
    const map = new Map();
    customers.forEach((customer) => map.set(String(customer.id), customer));
    return map;
  }, [customers]);

  const customerName = useCallback(
    (customerId) => customerById.get(String(customerId))?.name || "--",
    [customerById]
  );

  const refreshPlatform = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [customersPayload, onboardingPayload, sitesPayload, devicesPayload] = await Promise.all([
        fetch(apiUrl("/customers?action=list"), { headers: authHeaders() }).then(parseJson),
        fetch(apiUrl("/customers?action=onboarding"), { headers: authHeaders() }).then(parseJson),
        fetch(apiUrl(`/sites?t=${Date.now()}`), { headers: authHeaders() }).then(parseJson),
        fetch(apiUrl("/devices?action=list"), { headers: authHeaders() }).then(parseJson),
      ]);

      const nextCustomers = customersPayload.customers || [];
      setCustomers(nextCustomers);
      setOnboarding(onboardingPayload);
      setSites(Array.isArray(sitesPayload) ? sitesPayload : []);
      setDevices(devicesPayload.devices || []);

      const detailEntries = await Promise.all(
        nextCustomers.map(async (customer) => {
          const details = await fetch(apiUrl(`/customers?action=details&id=${customer.id}`), {
            headers: authHeaders(),
          }).then(parseJson);
          return [String(customer.id), details];
        })
      );
      setCustomerDetails(Object.fromEntries(detailEntries));
      setSelectedCustomerId((current) => current || nextCustomers[0]?.id || null);
    } catch (err) {
      setError(err.message || t.error);
    } finally {
      setLoading(false);
    }
  }, [t.error]);

  useEffect(() => {
    refreshPlatform();
  }, [refreshPlatform]);

  const selectedDetails = customerDetails[String(selectedCustomerId)] || null;

  const metrics = useMemo(() => {
    const blockedCustomers = customers.filter((customer) => customer.status === "blocked");
    const unassignedSites = sites.filter((site) => !site.customer_id);
    const enabledDevices = devices.filter((device) => Number(device.enabled) === 1);
    const disabledDevices = devices.filter((device) => Number(device.enabled) !== 1);
    return {
      blockedCustomers,
      unassignedSites,
      unassignedDevices: onboarding.unassigned_devices || [],
      cards: [
        [t.totalCustomers, customers.length, Building2],
        [t.activeCustomers, customers.filter((customer) => customer.status !== "blocked").length, CheckCircle2],
        [t.blockedCustomers, blockedCustomers.length, Ban],
        [t.totalSites, sites.length, Link2],
        [t.unassignedSites, unassignedSites.length, AlertTriangle],
        [t.totalDevices, devices.length, Server],
        [t.enabledDevices, enabledDevices.length, Power],
        [t.disabledDevices, disabledDevices.length, AlertTriangle],
      ],
    };
  }, [customers, devices, onboarding.unassigned_devices, sites, t]);

  const filteredCustomers = useMemo(() => {
    const needle = normalizeText(customerSearch);
    return customers.filter((customer) => {
      const matchesSearch =
        !needle ||
        normalizeText(customer.name).includes(needle) ||
        normalizeText(customer.legal_name).includes(needle) ||
        normalizeText(customer.business_id).includes(needle) ||
        normalizeText(customer.contact_name).includes(needle) ||
        String(customer.id).includes(needle);
      const matchesStatus = customerStatus === "all" || (customer.status || "active") === customerStatus;
      return matchesSearch && matchesStatus;
    });
  }, [customerSearch, customerStatus, customers]);

  const filteredSites = useMemo(() => {
    const needle = normalizeText(siteSearch);
    return sites.filter((site) => {
      const assigned = Boolean(site.customer_id);
      const matchesAssignment =
        siteAssignmentFilter === "all" ||
        (siteAssignmentFilter === "assigned" && assigned) ||
        (siteAssignmentFilter === "unassigned" && !assigned);
      const matchesSearch =
        !needle ||
        normalizeText(site.name).includes(needle) ||
        normalizeText(site.mac).includes(needle) ||
        normalizeText(customerName(site.customer_id)).includes(needle);
      return matchesAssignment && matchesSearch;
    });
  }, [customerName, siteAssignmentFilter, siteSearch, sites]);

  const filteredDevices = useMemo(() => {
    const needle = normalizeText(deviceSearch);
    return devices.filter((device) => {
      if (!needle) return true;
      return (
        normalizeText(device.mac).includes(needle) ||
        normalizeText(device.site_mac).includes(needle) ||
        normalizeText(device.site_name).includes(needle) ||
        normalizeText(customerName(device.customer_id)).includes(needle)
      );
    });
  }, [customerName, deviceSearch, devices]);

  const runAction = async (fn) => {
    setError("");
    try {
      await fn();
      await refreshPlatform();
    } catch (err) {
      setError(err.message || t.error);
    }
  };

  const createCustomer = (event) => {
    event.preventDefault();
    const payloadData = profileToPayload(createCustomerProfile);
    if (!payloadData.name) return;
    runAction(async () => {
      const payload = await fetch(apiUrl("/customers?action=create"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payloadData),
      }).then(parseJson);
      setCreateCustomerProfile(emptyCustomerProfile());
      setSelectedCustomerId(payload.customer?.id || null);
      setView("customers");
    });
  };

  const startEditCustomer = (customer) => {
    setEditingCustomerId(customer.id);
    setEditCustomerProfile(customerToProfile(customer));
  };

  const cancelEditCustomer = () => {
    setEditingCustomerId(null);
    setEditCustomerProfile(emptyCustomerProfile());
  };

  const updateCustomerProfile = (customerId) => {
    const payloadData = profileToPayload(editCustomerProfile);
    if (!payloadData.name) return;
    runAction(async () => {
      await fetch(apiUrl(`/customers?action=update&id=${customerId}`), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ ...payloadData, id: customerId }),
      }).then(parseJson);
      cancelEditCustomer();
    });
  };

  const deleteCustomer = (customer) => {
    if (!window.confirm(`${t.deleteCustomer}: ${customer.name}?\n\n${t.deleteCustomerWarning}`)) return;
    runAction(async () => {
      await fetch(apiUrl(`/customers?action=delete&id=${customer.id}`), {
        method: "DELETE",
        headers: authHeaders(),
      }).then(parseJson);
      setSelectedCustomerId(null);
      setEditingCustomerId(null);
    });
  };

  const setCustomerStatusAction = (customer, action) => {
    const warning = action === "block" ? t.blockWarning : t.reactivateWarning;
    if (!window.confirm(`${warning}\n\n${customer.name}`)) return;
    runAction(() =>
      fetch(apiUrl(`/customers?action=${action}&id=${customer.id}`), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ id: customer.id }),
      }).then(parseJson)
    );
  };

  const assignAdmin = (customerId) => {
    if (!assignAdminId) return;
    runAction(async () => {
      await fetch(apiUrl("/customers?action=assignAdmin"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ customer_id: customerId, user_id: assignAdminId }),
      }).then(parseJson);
      setAssignAdminId("");
    });
  };

  const unassignAdmin = (customerId, admin) => {
    if (!window.confirm(`${t.unassignAdmin}: ${admin.username}?`)) return;
    runAction(() =>
      fetch(apiUrl("/customers?action=unassignAdmin"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ customer_id: customerId, user_id: admin.id }),
      }).then(parseJson)
    );
  };

  const assignSite = (customerId, siteMac = assignSiteMac) => {
    if (!siteMac) return;
    runAction(async () => {
      await fetch(apiUrl("/customers?action=assignSite"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ customer_id: customerId, site_mac: siteMac }),
      }).then(parseJson);
      setAssignSiteMac("");
      setGlobalAssignSiteMac("");
      setGlobalAssignCustomerId("");
    });
  };

  const unassignSite = (customerId, site) => {
    if (!window.confirm(`${t.siteWarning}\n\n${site.name || site.mac}`)) return;
    runAction(() =>
      fetch(apiUrl("/customers?action=unassignSite"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ customer_id: customerId, site_mac: site.mac }),
      }).then(parseJson)
    );
  };

  const moveSite = (fromCustomerId, toCustomerId, site) => {
    if (!toCustomerId || String(fromCustomerId) === String(toCustomerId)) return;
    if (!window.confirm(`${t.siteWarning}\n\n${site.name || site.mac}`)) return;
    runAction(() =>
      fetch(apiUrl("/customers?action=moveSite"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          from_customer_id: fromCustomerId,
          to_customer_id: toCustomerId,
          site_mac: site.mac,
        }),
      }).then(parseJson)
    );
  };

  const provisionDevice = (event) => {
    event.preventDefault();
    runAction(async () => {
      const payload = await fetch(apiUrl("/devices?action=provision"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(provisionForm),
      }).then(parseJson);
      setOneTimeToken(payload.device_token ? { token: payload.device_token, mac: payload.device?.mac } : null);
      setProvisionForm({ mac: "", site_mac: "", device_name: "", serial_number: "", firmware_version: "" });
    });
  };

  const deviceAction = (action, device, extra = {}) => {
    const sensitive = action === "rotateToken" || action === "disable";
    if (sensitive && !window.confirm(`${action}: ${device.mac}?`)) return;
    runAction(async () => {
      const payload = await fetch(apiUrl(`/devices?action=${action}`), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ mac: device.mac, ...extra }),
      }).then(parseJson);
      if (payload.device_token) {
        setOneTimeToken({ token: payload.device_token, mac: payload.device?.mac || device.mac });
      }
    });
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-[#D7E5FF] bg-white p-8 text-center font-black text-[#617096]">
        {t.loading}
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-16">
      <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-end">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-[#D7E5FF] bg-white px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-[#1C3FE6]">
            <ShieldCheck size={14} />
            Super Admin
          </div>
          <h2 className="text-3xl font-black tracking-normal text-[#101633]">{t.title}</h2>
          <p className="mt-2 text-sm font-bold text-[#617096]">{t.subtitle}</p>
        </div>
        <button onClick={refreshPlatform} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#101633] px-4 py-3 text-sm font-black text-white">
          <RefreshCw size={17} />
          {t.refresh}
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-[#F2C7C2] bg-[#FFF1EF] px-4 py-3 text-sm font-black text-[#B43B31]">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {[
          ["overview", t.overview, ShieldCheck],
          ["customers", t.customers, Building2],
          ["sites", t.sites, Link2],
          ["devices", t.devices, Server],
        ].map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setView(key)}
            className={[
              "inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-black transition",
              view === key ? "border-[#1C3FE6] bg-[#1C3FE6] text-white" : "border-[#D7E5FF] bg-white text-[#243158] hover:border-[#8EB7FF]",
            ].join(" ")}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {view === "overview" && (
        <PlatformOverview
          t={t}
          language={language}
          metrics={metrics}
          sites={sites}
          devices={devices}
          customerName={customerName}
          onboarding={onboarding}
          setView={setView}
          setSelectedCustomerId={setSelectedCustomerId}
        />
      )}

      {view === "customers" && (
        <section className="min-w-0 space-y-5">
          <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
            <Panel title={t.customers}>
              <div className="mb-4 flex flex-col gap-3 md:flex-row">
                <SearchBox value={customerSearch} onChange={setCustomerSearch} placeholder={t.search} />
                <select value={customerStatus} onChange={(event) => setCustomerStatus(event.target.value)} className="w-full rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] px-3 py-3 text-sm font-bold outline-none md:w-auto">
                  <option value="all">{t.allStatuses}</option>
                  <option value="active">{t.active}</option>
                  <option value="blocked">{t.blocked}</option>
                </select>
              </div>
              <CustomerTable
                t={t}
                customers={filteredCustomers}
                details={customerDetails}
                selectedCustomerId={selectedCustomerId}
                onSelect={(id) => setSelectedCustomerId(id)}
              />
            </Panel>
            <Panel title={t.createCustomer}>
              <form onSubmit={createCustomer} className="space-y-3">
                <CustomerProfileForm
                  t={t}
                  value={createCustomerProfile}
                  onChange={setCreateCustomerProfile}
                />
                <button className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#101633] px-4 py-3 font-black text-white">
                  <Plus size={16} />
                  {t.create}
                </button>
              </form>
            </Panel>
          </div>
          {selectedDetails && (
            <CustomerDetails
              t={t}
              details={selectedDetails}
              customers={customers}
              onboarding={onboarding}
              assignAdminId={assignAdminId}
              setAssignAdminId={setAssignAdminId}
              assignSiteMac={assignSiteMac}
              setAssignSiteMac={setAssignSiteMac}
              onBlock={setCustomerStatusAction}
              onAssignAdmin={assignAdmin}
              onUnassignAdmin={unassignAdmin}
              onAssignSite={assignSite}
              onUnassignSite={unassignSite}
              onMoveSite={moveSite}
              editingCustomerId={editingCustomerId}
              editCustomerProfile={editCustomerProfile}
              setEditCustomerProfile={setEditCustomerProfile}
              onStartEdit={startEditCustomer}
              onCancelEdit={cancelEditCustomer}
              onSaveProfile={updateCustomerProfile}
              onDeleteCustomer={deleteCustomer}
            />
          )}
        </section>
      )}

      {view === "sites" && (
        <Panel title={t.sites}>
          <div className="mb-4 grid gap-3 xl:grid-cols-[1fr_180px_220px_220px_auto]">
            <SearchBox value={siteSearch} onChange={setSiteSearch} placeholder={t.search} />
            <select value={siteAssignmentFilter} onChange={(event) => setSiteAssignmentFilter(event.target.value)} className="rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] px-3 py-3 text-sm font-bold outline-none">
              <option value="all">{t.assignment}</option>
              <option value="assigned">{t.assigned}</option>
              <option value="unassigned">{t.unassigned}</option>
            </select>
            <select value={globalAssignSiteMac} onChange={(event) => setGlobalAssignSiteMac(event.target.value)} className="rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] px-3 py-3 text-sm font-bold outline-none">
              <option value="">{t.selectSite}</option>
              {(onboarding.unassigned_sites || []).map((site) => (
                <option key={site.mac} value={site.mac}>{site.name || site.mac}</option>
              ))}
            </select>
            <select value={globalAssignCustomerId} onChange={(event) => setGlobalAssignCustomerId(event.target.value)} className="rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] px-3 py-3 text-sm font-bold outline-none">
              <option value="">{t.selectCustomer}</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </select>
            <button onClick={() => assignSite(globalAssignCustomerId, globalAssignSiteMac)} disabled={!globalAssignSiteMac || !globalAssignCustomerId} className="rounded-xl bg-[#101633] px-4 py-3 text-sm font-black text-white disabled:opacity-40">
              {t.assignSite}
            </button>
          </div>
          <SitesTable t={t} sites={filteredSites} customers={customers} customerName={customerName} onOpenCustomer={(id) => { setSelectedCustomerId(id); setView("customers"); }} />
        </Panel>
      )}

      {view === "devices" && (
        <section className="space-y-5">
          <Panel title={t.provisionDevice}>
            <form onSubmit={provisionDevice} className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
              {[
                ["mac", t.deviceMac, true],
                ["site_mac", t.siteMac, true],
                ["device_name", t.deviceName, false],
                ["serial_number", t.serialNumber, false],
                ["firmware_version", t.firmwareVersion, false],
              ].map(([key, label, required]) => (
                <label key={key} className="block">
                  <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-[#617096]">{label}</span>
                  <input required={required} value={provisionForm[key]} onChange={(event) => setProvisionForm((prev) => ({ ...prev, [key]: event.target.value }))} className="w-full rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] px-3 py-3 text-sm font-bold outline-none focus:border-[#1C3FE6]" />
                </label>
              ))}
              <button className="self-end rounded-xl bg-[#101633] px-4 py-3 text-sm font-black text-white">{t.provision}</button>
            </form>
          </Panel>
          <Panel title={t.devices}>
            <div className="mb-4">
              <SearchBox value={deviceSearch} onChange={setDeviceSearch} placeholder={t.search} />
            </div>
            <DevicesTable
              t={t}
              devices={filteredDevices}
              sites={sites}
              customerName={customerName}
              deviceSiteTargets={deviceSiteTargets}
              setDeviceSiteTargets={setDeviceSiteTargets}
              onDeviceAction={deviceAction}
            />
          </Panel>
        </section>
      )}

      {oneTimeToken && (
        <TokenModal t={t} tokenInfo={oneTimeToken} onClose={() => setOneTimeToken(null)} />
      )}
    </div>
  );
};

const PlatformGlobalMap = ({ t, language, sites, devices, customerName }) => {
  const devicesBySite = useMemo(() => {
    const map = new Map();
    devices.forEach((device) => {
      const siteMac = String(device.site_mac || device.siteMac || "").trim();
      if (!siteMac) return;
      map.set(siteMac, [...(map.get(siteMac) || []), device]);
    });
    return map;
  }, [devices]);

  const mappedSites = useMemo(
    () =>
      sites
        .map((site) => {
          const siteDevices = devicesBySite.get(String(site.mac || "").trim()) || [];
          return {
            ...site,
            coordinates: getSiteCoordinates(site),
            mapStatus: getSiteMapStatus(site),
            siteDevices,
          };
        })
        .filter((site) => site.coordinates),
    [devicesBySite, sites]
  );

  const tileConfig = useMemo(() => getMapTileConfig(language), [language]);

  const unmappedCount = Math.max(0, sites.length - mappedSites.length);

  return (
    <section className="overflow-hidden rounded-2xl border border-[#D7E5FF] bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b border-[#E5EEFF] p-5 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-[#D7E5FF] bg-[#F8FBFF] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-[#1C3FE6]">
            <Globe2 size={14} />
            {t.globalMap}
          </div>
          <h3 className="text-2xl font-black text-[#101633]">{t.globalMapSubtitle}</h3>
        </div>
        <div className="flex flex-wrap gap-2 text-[10px] font-black uppercase tracking-[0.12em] text-[#617096]">
          <span className="rounded-full border border-[#BDEBD0] bg-[#F0FFF6] px-3 py-1.5 text-[#208653]">
            {mappedSites.length} {t.mappedSites}
          </span>
          <span className="rounded-full border border-[#F4D7A1] bg-[#FFF8E8] px-3 py-1.5 text-[#9A6200]">
            {unmappedCount} {t.unmappedSites}
          </span>
        </div>
      </div>

      <div className="relative h-[430px] min-h-[360px] md:h-[470px]">
        <MapContainer
          key={tileConfig.languageKey}
          center={WORLD_CENTER}
          zoom={WORLD_ZOOM}
          minZoom={2}
          maxZoom={18}
          scrollWheelZoom
          className="h-full w-full z-0"
          worldCopyJump
        >
          <TileLayer
            key={tileConfig.languageKey}
            attribution={tileConfig.attribution}
            url={tileConfig.url}
          />
          {mappedSites.map((site) => (
            <Marker
              key={site.mac}
              position={site.coordinates}
              icon={siteMarkerIcon(site.mapStatus)}
            >
              <Popup>
                <div className="min-w-[220px] text-slate-800">
                  <div className="text-sm font-black text-slate-950">
                    {site.name || site.mac}
                  </div>
                  <div className="mt-1 text-xs font-semibold text-slate-600">
                    {customerName(site.customer_id)}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <span className="font-bold">{t.mac}</span>
                    <span>{site.mac || t.notAvailable}</span>
                    <span className="font-bold">{t.status}</span>
                    <span>{t[site.mapStatus] || site.mapStatus}</span>
                    <span className="font-bold">{t.devices}</span>
                    <span>{site.siteDevices.length}</span>
                    <span className="font-bold">{t.lastContact}</span>
                    <span>{formatDisplayDate(site.last_heartbeat)}</span>
                  </div>
                  {site.siteDevices.length > 0 && (
                    <div className="mt-3 border-t border-slate-200 pt-2 text-xs text-slate-600">
                      {site.siteDevices.slice(0, 3).map((device) => (
                        <div key={device.mac || device.id} className="mb-1 last:mb-0">
                          <div className="font-semibold text-slate-800">
                            {device.device_name || device.name || device.mac}
                          </div>
                          <div>
                            {t.deviceState}: {isDeviceEnabled(device) ? t.enabled : t.disabled}
                            {device.last_authenticated_at ? ` · ${t.authSeen}: ${formatDisplayDate(device.last_authenticated_at)}` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {!mappedSites.length && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center bg-[#F8FBFF]/75 p-6 backdrop-blur-[1px]">
            <div className="max-w-sm rounded-2xl border border-[#D7E5FF] bg-white px-5 py-4 text-center shadow-sm">
              <div className="text-base font-black text-[#101633]">{t.noMappedSites}</div>
              <div className="mt-1 text-sm font-bold text-[#617096]">{t.noMappedSitesBody}</div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const PlatformOverview = ({ t, language, metrics, sites, devices, customerName, onboarding, setView, setSelectedCustomerId }) => (
  <div className="space-y-5">
    <section className="grid grid-cols-2 gap-4 xl:grid-cols-4">
      {metrics.cards.map(([label, value, Icon]) => (
        <div key={label} className="rounded-2xl border border-[#D7E5FF] bg-white p-4 shadow-sm">
          <span className="mb-3 grid h-10 w-10 place-items-center rounded-xl bg-[#EAF8FF] text-[#1C3FE6]">
            <Icon size={19} />
          </span>
          <div className="text-3xl font-black text-[#101633]">{value}</div>
          <div className="mt-1 text-[10px] font-black uppercase tracking-[0.13em] text-[#617096]">{label}</div>
        </div>
      ))}
    </section>
    <PlatformGlobalMap t={t} language={language} sites={sites} devices={devices} customerName={customerName} />
    <Panel title={t.attention}>
      <div className="grid gap-3 xl:grid-cols-3">
        {metrics.blockedCustomers.map((customer) => (
          <button key={customer.id} onClick={() => { setSelectedCustomerId(customer.id); setView("customers"); }} className="rounded-xl border border-[#F2C7C2] bg-[#FFF1EF] px-4 py-3 text-left font-black text-[#B43B31]">
            {customer.name}
          </button>
        ))}
        {(onboarding.unassigned_sites || []).slice(0, 6).map((site) => (
          <button key={site.mac} onClick={() => setView("sites")} className="rounded-xl border border-[#F4D7A1] bg-[#FFF8E8] px-4 py-3 text-left font-black text-[#9A6200]">
            {site.name || site.mac}
          </button>
        ))}
        {(onboarding.unassigned_devices || []).slice(0, 6).map((device) => (
          <button key={device.mac} onClick={() => setView("devices")} className="rounded-xl border border-[#F4D7A1] bg-[#FFF8E8] px-4 py-3 text-left font-black text-[#9A6200]">
            {device.mac}
          </button>
        ))}
        {metrics.blockedCustomers.length === 0 && (onboarding.unassigned_sites || []).length === 0 && (onboarding.unassigned_devices || []).length === 0 && (
          <div className="rounded-xl border border-[#BDEBD0] bg-[#F0FFF6] px-4 py-3 font-black text-[#208653]">{t.noAttention}</div>
        )}
      </div>
    </Panel>
  </div>
);

const CustomerProfileForm = ({ t, value, onChange }) => {
  const updateField = (key, fieldValue) => {
    onChange((current) => ({ ...current, [key]: fieldValue }));
  };

  return (
    <div className="space-y-3">
      {CUSTOMER_PROFILE_FIELDS.map((field) => {
        const commonClass = "w-full rounded-xl border border-[#D7E5FF] bg-white px-3 py-3 text-sm font-bold outline-none focus:border-[#1C3FE6]";
        return (
          <label key={field.key} className={field.internal ? "block rounded-xl border border-[#F4D7A1] bg-[#FFF8E8] p-3" : "block"}>
            <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-[#617096]">
              {t[field.labelKey]}{field.required ? " *" : ""}
            </span>
            {field.multiline ? (
              <textarea
                required={field.required}
                value={value[field.key] || ""}
                onChange={(event) => updateField(field.key, event.target.value)}
                rows={field.internal ? 4 : 3}
                className={`${commonClass} resize-y whitespace-pre-wrap`}
              />
            ) : (
              <input
                required={field.required}
                type={field.type || "text"}
                value={value[field.key] || ""}
                onChange={(event) => updateField(field.key, event.target.value)}
                className={commonClass}
              />
            )}
          </label>
        );
      })}
    </div>
  );
};

const CustomerProfileDisplay = ({ t, customer }) => {
  const visibleFields = CUSTOMER_PROFILE_FIELDS.filter((field) => field.key !== "name" && field.key !== "internal_notes" && customer[field.key]);
  const notes = customer.internal_notes;

  return (
    <div className="space-y-4">
      {visibleFields.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visibleFields.map((field) => (
            <div key={field.key} className="min-w-0 rounded-xl border border-[#D7E5FF] bg-white px-4 py-3">
              <div className="text-[10px] font-black uppercase tracking-[0.13em] text-[#617096]">{t[field.labelKey]}</div>
              <div className="mt-1 whitespace-pre-wrap break-words text-sm font-black text-[#101633]">{customer[field.key]}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-[#D7E5FF] bg-white px-4 py-3 text-sm font-black text-[#617096]">{t.noProfileFields}</div>
      )}
      {notes && (
        <div className="rounded-xl border border-[#F4D7A1] bg-[#FFF8E8] px-4 py-3">
          <div className="text-[10px] font-black uppercase tracking-[0.13em] text-[#9A6200]">{t.internal}: {t.internalNotes}</div>
          <div className="mt-2 whitespace-pre-wrap break-words text-sm font-bold text-[#5C4300]">{notes}</div>
        </div>
      )}
    </div>
  );
};

const CustomerDetails = ({
  t,
  details,
  customers,
  onboarding,
  assignAdminId,
  setAssignAdminId,
  assignSiteMac,
  setAssignSiteMac,
  onBlock,
  onAssignAdmin,
  onUnassignAdmin,
  onAssignSite,
  onUnassignSite,
  onMoveSite,
  editingCustomerId,
  editCustomerProfile,
  setEditCustomerProfile,
  onStartEdit,
  onCancelEdit,
  onSaveProfile,
  onDeleteCustomer,
}) => {
  const customer = details.customer;
  const isEditing = String(editingCustomerId) === String(customer.id);
  return (
    <Panel title={`${t.details}: ${customer.name}`}>
      <div className="mb-5 flex flex-col justify-between gap-3 xl:flex-row xl:items-center">
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge value={customer.status || "active"} label={(customer.status || "active") === "blocked" ? t.blocked : t.active} />
          <span className="text-sm font-bold text-[#617096]">ID {customer.id}</span>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button onClick={() => isEditing ? onCancelEdit() : onStartEdit(customer)} className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#D7E5FF] bg-white px-4 py-3 text-sm font-black text-[#1C3FE6]">
            {isEditing ? <X size={16} /> : <Edit3 size={16} />}
            {isEditing ? t.cancel : t.editProfile}
          </button>
          {customer.status === "blocked" ? (
            <button onClick={() => onBlock(customer, "reactivate")} className="rounded-xl bg-[#208653] px-4 py-3 text-sm font-black text-white">{t.reactivateCustomer}</button>
          ) : (
            <button onClick={() => onBlock(customer, "block")} className="rounded-xl bg-[#B43B31] px-4 py-3 text-sm font-black text-white">{t.blockCustomer}</button>
          )}
        </div>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 xl:grid-cols-4">
        <MiniStat label={t.assignedSites} value={details.summary?.sites || 0} />
        <MiniStat label={t.devices} value={details.summary?.devices || 0} />
        <MiniStat label={t.admins} value={details.summary?.admins || 0} />
        <MiniStat label={t.enabledDevices} value={details.summary?.enabled_devices || 0} />
      </div>

      <div className="mb-5 rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] p-4">
        <SectionTitle icon={<Building2 size={18} />} title={t.profile} />
        {isEditing ? (
          <div className="space-y-3">
            <CustomerProfileForm
              t={t}
              value={editCustomerProfile}
              onChange={setEditCustomerProfile}
            />
            <div className="flex flex-col gap-2 sm:flex-row">
              <button onClick={() => onSaveProfile(customer.id)} className="rounded-xl bg-[#101633] px-4 py-3 text-sm font-black text-white">
                {t.saveProfile}
              </button>
              <button onClick={onCancelEdit} className="rounded-xl border border-[#D7E5FF] bg-white px-4 py-3 text-sm font-black text-[#617096]">
                {t.cancel}
              </button>
            </div>
          </div>
        ) : (
          <CustomerProfileDisplay t={t} customer={customer} />
        )}
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <div>
          <SectionTitle icon={<Users size={18} />} title={t.customerAdmins} />
          <div className="mb-3 flex flex-col gap-2 sm:flex-row">
            <select value={assignAdminId} onChange={(event) => setAssignAdminId(event.target.value)} className="min-w-0 flex-1 rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] px-3 py-3 text-sm font-bold outline-none">
              <option value="">{t.availableAdmins}</option>
              {(onboarding.admin_candidates || []).map((admin) => (
                <option key={admin.id} value={admin.id}>{admin.username}</option>
              ))}
            </select>
            <button onClick={() => onAssignAdmin(customer.id)} disabled={!assignAdminId} className="inline-flex justify-center rounded-xl bg-[#101633] px-4 py-3 text-sm font-black text-white disabled:opacity-40">
              <UserPlus size={16} />
            </button>
          </div>
          <SimpleList empty={t.noAdmins}>
            {(details.admins || []).map((admin) => (
              <div key={admin.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] px-4 py-3">
                <div>
                  <div className="font-black text-[#101633]">{admin.username}</div>
                  <div className="text-xs font-bold text-[#617096]">{admin.email || admin.role}</div>
                </div>
                <button onClick={() => onUnassignAdmin(customer.id, admin)} className="rounded-lg border border-[#F2C7C2] bg-white p-2 text-[#B43B31]">
                  <UserMinus size={16} />
                </button>
              </div>
            ))}
          </SimpleList>
        </div>

        <div>
          <SectionTitle icon={<Link2 size={18} />} title={t.assignedSites} />
          <div className="mb-3 flex flex-col gap-2 sm:flex-row">
            <select value={assignSiteMac} onChange={(event) => setAssignSiteMac(event.target.value)} className="min-w-0 flex-1 rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] px-3 py-3 text-sm font-bold outline-none">
              <option value="">{t.selectSite}</option>
              {(onboarding.unassigned_sites || []).map((site) => (
                <option key={site.mac} value={site.mac}>{site.name || site.mac}</option>
              ))}
            </select>
            <button onClick={() => onAssignSite(customer.id)} disabled={!assignSiteMac} className="inline-flex justify-center rounded-xl bg-[#101633] px-4 py-3 text-sm font-black text-white disabled:opacity-40">
              <Plus size={16} />
            </button>
          </div>
          <SimpleList empty={t.noSites}>
            {(details.sites || []).map((site) => (
              <div key={site.mac} className="rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] px-4 py-3">
                <div className="flex flex-col justify-between gap-3 xl:flex-row xl:items-center">
                  <div>
                    <div className="font-black text-[#101633]">{site.name || site.mac}</div>
                    <div className="text-xs font-bold text-[#617096]">{site.mac}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <select onChange={(event) => onMoveSite(customer.id, event.target.value, site)} defaultValue="" className="rounded-lg border border-[#D7E5FF] bg-white px-2 py-2 text-xs font-bold">
                      <option value="">{t.moveSite}</option>
                      {customers.filter((item) => String(item.id) !== String(customer.id)).map((item) => (
                        <option key={item.id} value={item.id}>{item.name}</option>
                      ))}
                    </select>
                    <button onClick={() => onUnassignSite(customer.id, site)} className="rounded-lg border border-[#F2C7C2] bg-white px-3 py-2 text-xs font-black text-[#B43B31]">
                      {t.unassignSite}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </SimpleList>
        </div>
      </div>
      <div className="mt-5 rounded-xl border border-[#F2C7C2] bg-[#FFF1EF] p-4">
        <div className="flex flex-col justify-between gap-3 xl:flex-row xl:items-center">
          <div>
            <div className="text-sm font-black text-[#B43B31]">{t.deleteCustomer}</div>
            <div className="mt-1 text-xs font-bold text-[#9D5149]">{t.deleteCustomerWarning}</div>
          </div>
          <button onClick={() => onDeleteCustomer(customer)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#B43B31] px-4 py-3 text-sm font-black text-white">
            <Trash2 size={16} />
            {t.deleteCustomer}
          </button>
        </div>
      </div>
    </Panel>
  );
};

const CustomerTable = ({ t, customers, details, selectedCustomerId, onSelect }) => (
  <DataTable empty={t.noCustomers} columns={[t.customerName, t.status, t.assignedSites, t.devices, t.admins, ""]}>
    {customers.map((customer) => {
      const detail = details[String(customer.id)] || {};
      return (
        <tr key={customer.id} className={String(selectedCustomerId) === String(customer.id) ? "bg-[#EAF8FF]" : ""}>
          <td className="px-4 py-3 font-black text-[#101633]">{customer.name}</td>
          <td className="px-4 py-3"><StatusBadge value={customer.status || "active"} label={(customer.status || "active") === "blocked" ? t.blocked : t.active} /></td>
          <td className="px-4 py-3 font-bold">{detail.summary?.sites ?? "--"}</td>
          <td className="px-4 py-3 font-bold">{detail.summary?.devices ?? "--"}</td>
          <td className="px-4 py-3 font-bold">{(detail.admins || []).map((admin) => admin.username).join(", ") || "--"}</td>
          <td className="px-4 py-3 text-right">
            <button onClick={() => onSelect(customer.id)} className="rounded-lg bg-[#101633] px-3 py-2 text-xs font-black text-white">{t.openDetails}</button>
          </td>
        </tr>
      );
    })}
  </DataTable>
);

const SitesTable = ({ t, sites, customerName, onOpenCustomer }) => (
  <DataTable empty={t.noSites} columns={[t.site, t.customer, t.assignment, t.deviceCount, t.animalCount, ""]}>
    {sites.map((site) => (
      <tr key={site.mac}>
        <td className="px-4 py-3">
          <div className="font-black text-[#101633]">{site.name || site.mac}</div>
          <div className="text-xs font-bold text-[#617096]">{site.mac}</div>
        </td>
        <td className="px-4 py-3 font-bold">{site.customer_id ? customerName(site.customer_id) : "--"}</td>
        <td className="px-4 py-3"><StatusBadge value={site.customer_id ? "assigned" : "unassigned"} label={site.customer_id ? t.assigned : t.unassigned} /></td>
        <td className="px-4 py-3 font-bold">{site.devicesCount ?? site.devices_count ?? 1}</td>
        <td className="px-4 py-3 font-bold">{site.animalsCount ?? site.animals_count ?? site.dbCount ?? "--"}</td>
        <td className="px-4 py-3 text-right">
          {site.customer_id && <button onClick={() => onOpenCustomer(site.customer_id)} className="rounded-lg bg-[#101633] px-3 py-2 text-xs font-black text-white">{t.openDetails}</button>}
        </td>
      </tr>
    ))}
  </DataTable>
);

const DevicesTable = ({ t, devices, sites, customerName, deviceSiteTargets, setDeviceSiteTargets, onDeviceAction }) => (
  <DataTable empty={t.noDevices} columns={[t.deviceMac, t.customer, t.site, t.status, t.firmwareVersion, t.assignDevice, ""]}>
    {devices.map((device) => (
      <tr key={device.mac}>
        <td className="px-4 py-3">
          <div className="font-black text-[#101633]">{device.mac}</div>
          <div className="text-xs font-bold text-[#617096]">{device.device_name || device.serial_number || "--"}</div>
        </td>
        <td className="px-4 py-3 font-bold">{device.customer_id ? customerName(device.customer_id) : "--"}</td>
        <td className="px-4 py-3 font-bold">{device.site_name || device.site_mac}</td>
        <td className="px-4 py-3"><StatusBadge value={Number(device.enabled) === 1 ? "enabled" : "disabled"} label={Number(device.enabled) === 1 ? t.enabled : t.disabled} /></td>
        <td className="px-4 py-3 font-bold">{device.firmware_version || "--"}</td>
        <td className="px-4 py-3">
          <div className="flex gap-2">
            <select value={deviceSiteTargets[device.mac] || ""} onChange={(event) => setDeviceSiteTargets((prev) => ({ ...prev, [device.mac]: event.target.value }))} className="min-w-[170px] rounded-lg border border-[#D7E5FF] bg-white px-2 py-2 text-xs font-bold">
              <option value="">{t.selectSite}</option>
              {sites.map((site) => (
                <option key={site.mac} value={site.mac}>{site.name || site.mac}</option>
              ))}
            </select>
            <button onClick={() => onDeviceAction("assignSite", device, { site_mac: deviceSiteTargets[device.mac] })} disabled={!deviceSiteTargets[device.mac]} className="rounded-lg bg-[#101633] px-3 py-2 text-xs font-black text-white disabled:opacity-40">
              <Link2 size={14} />
            </button>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap justify-end gap-2">
            <button onClick={() => onDeviceAction("rotateToken", device)} className="rounded-lg border border-[#D7E5FF] bg-white p-2 text-[#1C3FE6]" title={t.rotateToken}><KeyRound size={15} /></button>
            {Number(device.enabled) === 1 ? (
              <button onClick={() => onDeviceAction("disable", device)} className="rounded-lg border border-[#F2C7C2] bg-white p-2 text-[#B43B31]" title={t.disable}><Ban size={15} /></button>
            ) : (
              <button onClick={() => onDeviceAction("enable", device)} className="rounded-lg border border-[#BDEBD0] bg-white p-2 text-[#208653]" title={t.enable}><Power size={15} /></button>
            )}
          </div>
        </td>
      </tr>
    ))}
  </DataTable>
);

const TokenModal = ({ t, tokenInfo, onClose }) => (
  <div className="fixed inset-0 z-50 grid place-items-center bg-[#101633]/60 p-4">
    <div className="w-full max-w-2xl rounded-2xl border border-[#D7E5FF] bg-white p-5 shadow-2xl">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-xl font-black text-[#101633]">{t.oneTimeToken}</h3>
          <p className="mt-1 text-sm font-bold text-[#B43B31]">{t.tokenNotice}</p>
        </div>
        <button onClick={onClose} className="rounded-xl border border-[#D7E5FF] p-2 text-[#617096]"><X size={18} /></button>
      </div>
      <div className="mb-3 text-sm font-black text-[#617096]">{tokenInfo.mac}</div>
      <div className="break-all rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] p-4 font-mono text-sm font-bold text-[#101633]">{tokenInfo.token}</div>
      <button onClick={onClose} className="mt-4 w-full rounded-xl bg-[#101633] px-4 py-3 font-black text-white">{t.close}</button>
    </div>
  </div>
);

const Panel = ({ title, children }) => (
  <section className="min-w-0 max-w-full rounded-2xl border border-[#D7E5FF] bg-white p-4 shadow-sm sm:p-5">
    <h3 className="mb-4 text-xl font-black text-[#101633]">{title}</h3>
    {children}
  </section>
);

const SectionTitle = ({ icon, title }) => (
  <div className="mb-3 flex items-center gap-2 text-sm font-black uppercase tracking-[0.12em] text-[#617096]">
    {icon}
    {title}
  </div>
);

const MiniStat = ({ label, value }) => (
  <div className="rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] px-4 py-3">
    <div className="text-2xl font-black text-[#101633]">{value}</div>
    <div className="text-[10px] font-black uppercase tracking-[0.13em] text-[#617096]">{label}</div>
  </div>
);

const StatusBadge = ({ value, label }) => (
  <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-black ${statusTone[value] || statusTone.assigned}`}>
    {label || value}
  </span>
);

const SearchBox = ({ value, onChange, placeholder }) => (
  <label className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] px-3 py-3">
    <Search size={17} className="text-[#617096]" />
    <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} className="min-w-0 flex-1 bg-transparent text-sm font-bold outline-none" />
  </label>
);

const SimpleList = ({ empty, children }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  if (items.length === 0) {
    return <div className="rounded-xl border border-[#D7E5FF] bg-[#F8FBFF] px-4 py-4 text-sm font-black text-[#617096]">{empty}</div>;
  }
  return <div className="space-y-2">{items}</div>;
};

const DataTable = ({ columns, children, empty }) => {
  const rows = React.Children.toArray(children).filter(Boolean);
  return (
    <div className="max-w-full overflow-hidden rounded-xl border border-[#D7E5FF]">
      <div className="tw-data-table-scroll overflow-x-auto">
        <table className="tw-data-table min-w-[720px] text-left text-sm">
          <thead className="bg-[#F5F9FF] text-[10px] font-black uppercase tracking-[0.14em] text-[#617096]">
            <tr>
              {columns.map((column) => (
                <th key={column} className="px-4 py-3">{column}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EDF3FF]">
            {rows.length > 0 ? rows : (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center font-black text-[#617096]">{empty}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MasterDashboard;
